import { initializeApp } from "firebase/app";
import { getAnalytics } from "firebase/analytics";
import {
  initializeAppCheck,
  ReCaptchaV3Provider,
  ReCaptchaEnterpriseProvider,
  getToken as getAppCheckToken,
} from "firebase/app-check";
import {
  //getFirestore,
  connectFirestoreEmulator,
  initializeFirestore,
  persistentLocalCache,
} from "firebase/firestore";
import { getAuth, connectAuthEmulator } from "firebase/auth";
import { getFunctions, connectFunctionsEmulator } from "firebase/functions";
import { getStorage } from "firebase/storage";

const config = {
  apiKey: process.env.REACT_APP_API_KEY,
  authDomain: process.env.REACT_APP_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_PROJECT_ID,
  storageBucket: process.env.REACT_APP_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_APP_ID,
  measurementId: process.env.REACT_APP_MEASUREMENT_ID,
};

const app = initializeApp(config);

// Hosts that should bypass real reCAPTCHA in favor of an App Check
// debug token. Includes localhost loopbacks and the dev machine's LAN
// IP so phones/tablets on the same network can hit the dev server.
const DEV_HOSTS = ["localhost", "127.0.0.1", "192.168.1.170"];
const isDevHost =
  typeof window !== "undefined" &&
  DEV_HOSTS.includes(window.location.hostname);

// Initialize App Check before any other Firebase service so all subsequent
// Firebase requests automatically attach the App Check token.
//
// On a dev host we set FIREBASE_APPCHECK_DEBUG_TOKEN so the SDK uses a
// debug token instead of attempting real reCAPTCHA. Two modes:
//   - REACT_APP_APPCHECK_DEBUG_TOKEN unset: assign `true` and the SDK
//     auto-generates a fresh token (logged once to console) for you to
//     register in Firebase Console > App Check > Manage debug tokens.
//   - REACT_APP_APPCHECK_DEBUG_TOKEN set: assign that exact UUID so the
//     same registered token is reused on every reload and across dev
//     hosts (no copy/paste churn).
const recaptchaSiteKey = (process.env.REACT_APP_RECAPTCHA_SITE_KEY || "").trim();
const recaptchaProviderType = (
  process.env.REACT_APP_RECAPTCHA_PROVIDER || "v3"
)
  .trim()
  .toLowerCase();
const appCheckDebugToken = (
  process.env.REACT_APP_APPCHECK_DEBUG_TOKEN || ""
).trim();
let appCheckInstance = null;

if (recaptchaSiteKey) {
  if (isDevHost) {
    if (appCheckDebugToken) {
      // eslint-disable-next-line no-restricted-globals
      self.FIREBASE_APPCHECK_DEBUG_TOKEN = appCheckDebugToken;
      // eslint-disable-next-line no-console
      console.info(
        "[AppCheck] Using configured debug token (dev host: " +
          window.location.hostname +
          ")."
      );
    } else {
      // eslint-disable-next-line no-restricted-globals
      self.FIREBASE_APPCHECK_DEBUG_TOKEN = true;
    }
  }
  try {
    const provider =
      recaptchaProviderType === "enterprise"
        ? new ReCaptchaEnterpriseProvider(recaptchaSiteKey)
        : new ReCaptchaV3Provider(recaptchaSiteKey);
    appCheckInstance = initializeAppCheck(app, {
      provider,
      isTokenAutoRefreshEnabled: true,
    });

    // Startup diagnostic: verify we can actually obtain a token.
    // A failure here (wrong key, domain not allowlisted) means every
    // enforced callable will return 401.  We surface it loudly so the
    // issue is obvious before a user hits it.
    if (!isDevHost && process.env.NODE_ENV !== "test") {
      getAppCheckToken(appCheckInstance, /* forceRefresh */ false)
        .then(() => {
          console.info("[AppCheck] Token acquired successfully at startup.");
        })
        .catch((err) => {
          const keyPrefix = recaptchaSiteKey.slice(0, 6);
          const hostname =
            typeof window !== "undefined" ? window.location.hostname : "(ssr)";
          // eslint-disable-next-line no-console
          console.error(
            "[AppCheck] STARTUP TOKEN FAILURE — all Cloud Function calls will " +
              "return 401 until this is resolved.\n" +
              "Diagnostics:\n" +
              `  providerType   = ${recaptchaProviderType}\n` +
              `  siteKeyLength  = ${recaptchaSiteKey.length}\n` +
              `  siteKeyPrefix  = ${keyPrefix}…\n` +
              `  firebaseAppId  = ${config.appId}\n` +
              `  hostname       = ${hostname}\n` +
              "Checklist:\n" +
              "  1. REACT_APP_RECAPTCHA_SITE_KEY matches the key in Google reCAPTCHA Admin.\n" +
              "  2. Both usvipiratesguild.com AND www.usvipiratesguild.com are listed as\n" +
              "     allowed domains in the reCAPTCHA Admin Console.\n" +
              "  3. The same domains are registered under Firebase App Check for this app.\n" +
              "  4. The production build was compiled with the correct REACT_APP_RECAPTCHA_SITE_KEY.\n" +
              "  5. If the key is reCAPTCHA Enterprise, set REACT_APP_RECAPTCHA_PROVIDER=enterprise.\n" +
              "Error:",
            err?.message || err
          );
        });
    }
  } catch (err) {
    // Initialization can throw if called more than once (e.g. during HMR);
    // log and continue so the app still boots.
    // eslint-disable-next-line no-console
    console.warn("App Check initialization warning:", err?.message || err);
  }
} else if (process.env.NODE_ENV !== "test") {
  // eslint-disable-next-line no-console
  console.error(
    "[AppCheck] REACT_APP_RECAPTCHA_SITE_KEY is not set — App Check is " +
      "disabled and calls to enforced Cloud Functions WILL fail in production. " +
      "Add the variable to your .env file and rebuild."
  );
}

// Initialize Firestore with persistence settings.
//
// NOTE: We intentionally do NOT use persistentMultipleTabManager(). The
// multi-tab manager in firebase-js-sdk 10.14.x is the documented trigger
// for "INTERNAL ASSERTION FAILED: Unexpected state" inside TargetState /
// WatchChangeAggregator (see firebase-js-sdk issues #7239, #8250, #8389),
// which surfaces on pages that mount onSnapshot listeners under
// React.StrictMode's double-mount. Single-tab persistent cache preserves
// offline support without the buggy watch-target path.
const firestore = initializeFirestore(app, {
  localCache: persistentLocalCache(),
});
const auth = getAuth(app);
const storage = getStorage(app);
const functions = getFunctions(app);
//const firestore = getFirestore(app);
const analytics = getAnalytics(app);

// Set the region for your functions (important!)
functions.region = "us-central1";

// Connect to Firebase emulator if in development
if (isDevHost && process.env.REACT_APP_USE_FIREBASE_EMULATOR === "true") {
  console.log("Running on dev host - connecting to Firebase emulators");
  connectAuthEmulator(auth, "http://localhost:9099");
  connectFirestoreEmulator(firestore, "localhost", 5002);
  connectFunctionsEmulator(functions, "localhost", 5001);
} else {
  console.log("Running in production mode or emulator use disabled");
}

export { app, firestore, auth, storage, functions, analytics };
